import { Group, Table, Text } from '@mantine/core'
import { IconDeviceDesktop } from '@tabler/icons-react'
import { useMemo } from 'react'
import { GpuBadgeList } from '@/components/devices/GpuList/GpuBadgeList'
import { OnlineStatus } from '@/components/devices/OnlineStatus/OnlineStatus'
import {
  DeploymentOverviewApp,
  DeploymentOverviewDevice
} from '@/types/deploymentGroup'
import { Device } from '@/types/device'
import { getDeviceGpus, isJetsonDevice } from '@/utils/device/getDeviceAttr'
import { DeploymentListItem } from '../DeploymentList/DeploymentListItem'
import { DeploymentGroupRow } from './DeploymentGroupRow'
import { getGroupLabel, sortGroupsByLabel } from './deploymentTreeHelpers'
import { DeploymentTreeSlots } from './deploymentTreeTypes'

const COLUMN_COUNT = 4

type DeploymentDeviceRowsProps = DeploymentTreeSlots & {
  app: DeploymentOverviewApp
  overviewDevice: DeploymentOverviewDevice
  device?: Device
  groupsEnabled: boolean
  highlightedStreamId?: string
  highlightedGroupId?: string
}

export const DeploymentDeviceRows = ({
  app,
  overviewDevice,
  device,
  groupsEnabled,
  highlightedStreamId,
  highlightedGroupId,
  renderGroupActions,
  renderMemberActions,
  onOpenGroup
}: DeploymentDeviceRowsProps) => {
  const deviceName = device?.label ?? overviewDevice.device_id

  const groups = useMemo(
    () => sortGroupsByLabel(overviewDevice.groups),
    [overviewDevice.groups]
  )

  return (
    <>
      <Table.Tr bg="gray.0">
        <Table.Td colSpan={COLUMN_COUNT} pl="lg">
          <Group gap="sm" wrap="nowrap">
            <IconDeviceDesktop size={16} />

            <Text size="sm" fw={600}>
              {deviceName}
            </Text>

            {device && (
              <OnlineStatus
                status={device.active ? 'online' : 'offline'}
                withText={false}
              />
            )}

            {device && (
              <GpuBadgeList
                gpus={getDeviceGpus(device)}
                isJetson={isJetsonDevice(device)}
              />
            )}
          </Group>
        </Table.Td>
      </Table.Tr>

      {groups.map((group) => {
        const context = {
          app,
          device,
          deviceName,
          label: getGroupLabel(group, groups)
        }

        return (
          <DeploymentGroupRow
            key={group.id}
            group={group}
            label={context.label}
            device={device}
            highlightedStreamId={highlightedStreamId}
            defaultExpanded={group.id === highlightedGroupId}
            renderGroupActions={
              renderGroupActions && ((g) => renderGroupActions(g, context))
            }
            renderMemberActions={
              renderMemberActions &&
              ((m, g) => renderMemberActions(m, g, context))
            }
            onOpenGroup={onOpenGroup}
          />
        )
      })}

      {overviewDevice.single_deployments.map((deployment) => (
        <DeploymentListItem
          key={deployment.id}
          deployment={deployment}
          device={device}
          groupsEnabled={groupsEnabled}
        />
      ))}
    </>
  )
}
